/**
 * One-shot migration: rename every `xaes2-*` persistence key to its
 * `xaes-*` canonical name. Runs once at app boot before any store
 * hydrates so the stores never see the legacy key namespace.
 *
 * Why:
 *   - The original React rebuild deliberately namespaced everything
 *     under `xaes2-*` to avoid colliding with the legacy static-HTML
 *     app's `xaes-*` keys (mc.html / wd.html stored theme + auth +
 *     power-user state under `xaes-*`).
 *   - With Phase 5b underway, `_legacy/` is no longer mounted in the
 *     browser. The temporary `xaes2-*` prefix can collapse back to
 *     the cleaner canonical `xaes-*`.
 *
 * The migration is idempotent — a marker key (`xaes-migrated-from-
 * xaes2`) is written on success so subsequent boots skip the sweep.
 *
 * Best-effort throughout: a missing legacy key, an idb permission
 * failure, or a localStorage quota error doesn't block boot. The
 * app degrades to an empty store rather than crashing.
 */

import {
  createStore,
  get as idbGet,
  set as idbSet,
  del as idbDel,
  keys as idbKeys,
} from "idb-keyval";

/** Localstorage keys: simple `xaes2-X` → `xaes-X` renames. */
const LOCAL_STORAGE_KEYS: ReadonlyArray<readonly [string, string]> = [
  ["xaes2-theme", "xaes-theme"],
  ["xaes2-auth", "xaes-auth"],
  ["xaes2-power", "xaes-power"],
  ["xaes2-users", "xaes-users"],
  ["xaes2-claude-settings", "xaes-claude-settings"],
];

/** idb-keyval flat keys: simple `xaes2-X` → `xaes-X` renames. */
const IDB_FLAT_KEYS: ReadonlyArray<readonly [string, string]> = [
  ["xaes2-asset-locker", "xaes-asset-locker"],
  ["xaes2-asset-locker-seeded", "xaes-asset-locker-seeded"],
  ["xaes2-captures", "xaes-captures"],
];

/**
 * idb-keyval namespaced prefixes — keys are `${prefix}:${rest}`. We
 * enumerate `idbKeys()` and rename every match in one pass.
 */
const IDB_PREFIXES: ReadonlyArray<readonly [string, string]> = [
  ["xaes2-chat-sessions:", "xaes-chat-sessions:"],
  ["xaes2-snapshots:", "xaes-snapshots:"],
];

/** Migration marker — once set, the sweep skips on subsequent boots. */
const MIGRATION_MARKER = "xaes-migrated-from-xaes2";

export async function runStorageMigration(): Promise<void> {
  if (typeof window === "undefined") return;
  // Marker lives in localStorage so it's resolvable synchronously on
  // every boot (idb-keyval requires await; localStorage doesn't).
  if (localStorage.getItem(MIGRATION_MARKER) === "v1") return;

  try {
    migrateLocalStorage();
    await migrateIdbFlat();
    await migrateIdbPrefixed();
    await migrateBlobStore();
  } catch {
    // Best-effort — a single failure shouldn't block subsequent runs
    // since the marker isn't set on the failure path. The user can
    // retry by reloading; on the next boot we re-attempt.
    return;
  }

  try {
    localStorage.setItem(MIGRATION_MARKER, "v1");
  } catch {
    /* localStorage quota — marker won't be set; we'll retry next boot. */
  }
}

function migrateLocalStorage(): void {
  for (const [oldKey, newKey] of LOCAL_STORAGE_KEYS) {
    const value = localStorage.getItem(oldKey);
    if (value === null) continue;
    // If the user already has a value under the new key (e.g. a fresh
    // install ran the new store before the migration), keep the new
    // one — it's more recent / intentional.
    if (localStorage.getItem(newKey) === null) {
      try {
        localStorage.setItem(newKey, value);
      } catch {
        continue;
      }
    }
    try {
      localStorage.removeItem(oldKey);
    } catch {
      /* leave it; the new key wins on subsequent reads. */
    }
  }
}

async function migrateIdbFlat(): Promise<void> {
  for (const [oldKey, newKey] of IDB_FLAT_KEYS) {
    const value = await idbGet(oldKey);
    if (value === undefined) continue;
    const existing = await idbGet(newKey);
    if (existing === undefined) {
      await idbSet(newKey, value);
    }
    await idbDel(oldKey);
  }
}

/**
 * Blob store sits in its own IndexedDB database (see
 * `src/lib/blob-store.ts`). The DB itself was renamed from
 * `xaes2-blobs` → `xaes-blobs`. IDB has no rename primitive, so we
 * open both DBs, copy any payloads forward, and best-effort wipe the
 * old DB once the copy succeeds. A user whose snapshots were stored
 * in `xaes2-blobs` keeps every previewable thumbnail; a fresh install
 * with no legacy DB is a noop.
 */
async function migrateBlobStore(): Promise<void> {
  const oldStore = createStore("xaes2-blobs", "blobs");
  const newStore = createStore("xaes-blobs", "blobs");
  const oldKeys = await idbKeys(oldStore);
  if (oldKeys.length === 0) return;
  for (const rawKey of oldKeys) {
    if (typeof rawKey !== "string") continue;
    const existing = await idbGet(rawKey, newStore);
    if (existing !== undefined) continue;
    const blob = await idbGet(rawKey, oldStore);
    if (blob === undefined) continue;
    await idbSet(rawKey, blob, newStore);
  }
  // Sweep the old DB so the user doesn't pay storage twice. Drop the
  // entries first (some browsers won't actually free space until the
  // entire DB closes; idb-keyval doesn't expose `deleteDB`, but
  // emptying the store is good enough for our purposes).
  for (const rawKey of oldKeys) {
    if (typeof rawKey !== "string") continue;
    await idbDel(rawKey, oldStore);
  }
}

async function migrateIdbPrefixed(): Promise<void> {
  const allKeys = await idbKeys();
  for (const rawKey of allKeys) {
    if (typeof rawKey !== "string") continue;
    for (const [oldPrefix, newPrefix] of IDB_PREFIXES) {
      if (!rawKey.startsWith(oldPrefix)) continue;
      const newKey = newPrefix + rawKey.slice(oldPrefix.length);
      const value = await idbGet(rawKey);
      if (value === undefined) break;
      const existing = await idbGet(newKey);
      if (existing === undefined) {
        await idbSet(newKey, value);
      }
      await idbDel(rawKey);
      break;
    }
  }
}
